import { Observable } from 'rxjs';
import { IDimensions, IViewState } from '@newroom-connect/library/interfaces';

/**
 * Scene states enumeration for lifecycle management.
 */
export enum SceneState {
  IDLE = 'idle',
  INITIALIZING = 'initializing',
  LOADING = 'loading',
  READY = 'ready',
  ERROR = 'error',
  DISPOSED = 'disposed'
}

/**
 * Scene events enumeration for event handling.
 */
export enum SceneEvent {
  STATE_CHANGE = 'stateChange',
  LOADING_PROGRESS = 'loadingProgress',
  LOADING_COMPLETE = 'loadingComplete',
  LOADING_ERROR = 'loadingError',
  VIEW_CHANGE = 'viewChange',
  RESIZE = 'resize',
  ERROR = 'error',
  READY = 'ready',
  DISPOSED = 'disposed',
  HOTSPOT_ADDED = 'hotspotAdded',
  HOTSPOT_REMOVED = 'hotspotRemoved',
  CONTAINER_RESIZED = 'containerResized'
}

/**
 * Generic event interface for scene events.
 */
export interface IEvent<T = unknown> {
  type: SceneEvent;
  data?: T;
  timestamp: number;
}

/**
 * Universal scene interface implemented by all scene types.
 * Provides consistent API for scene management regardless of type.
 */
export interface IScene<TConfig = ISceneConfigInput, TState = IViewState> {
  readonly isInitialized: boolean;
  readonly state$: Observable<SceneState>;
  readonly events$: Observable<IEvent>;

  // Lifecycle methods
  initialize(container: HTMLElement, config?: TConfig): Promise<void>;
  dispose(): void;

  // State management
  getCurrentCameraState(): TState;

  // Event handling
  on<T = unknown>(eventType: SceneEvent): Observable<IEvent<T>>;
}

/**
 * Universal scene state interface for all scene types.
 */
export interface ISceneState {
  isReady: boolean;
  hasError: boolean;
  loadingProgress?: ILoadingProgress;
  dimensions?: IDimensions;
  viewState?: IViewState;
}

/**
 * Simplified configuration input for initializing a scene.
 * Marzipano-specific configurations should be handled in IMarzipanoSceneConfig.
 */
export interface ISceneConfigInput {
  initialView?: IViewState;
}

/**
 * Possible states for scene loading operations.
 */
export type SceneLoadingStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Contains detailed information about the current loading progress.
 */
export interface ILoadingProgress {
  loaded: number;
  total: number;
  percentage: number;
  status: SceneLoadingStatus;
}
